import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Check, ChevronDown, PlusCircle, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Separator } from "./ui/separator";

type TextFilter = {
  type: "text";
  value: string | null;
  displayValue?: string;
  key: string;
  onChange: (value: string) => void;
  onClear: () => void;
};

type SelectFilter = {
  type: "select";
  value: string | null;
  displayValue?: string;
  options: string[];
  key: string;
  onChange: (value: string) => void;
  onClear: () => void;
};

type ComboboxFilter = {
  type: "combobox";
  value: string | null;
  displayValue?: string;
  options: string[];
  key: string;
  onChange: (value: string) => void;
  onClear: () => void;
  onOpen: (open: boolean) => void;
};

type Input = {
  label: string;
  filter: TextFilter | SelectFilter | ComboboxFilter;
};

function ComboboxFilterForm({ filter, label }: { filter: ComboboxFilter; label: string }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    console.log("open", open);
    filter.onOpen(open);
  }, [open]);

  return (
    <div className="flex flex-col gap-2">
      <Command>
        <CommandList>
          <CommandInput placeholder={`Search ${label.toLowerCase()}...`} />
          <CommandEmpty>{filter.options.length === 0 ? "" : "No results found."}</CommandEmpty>
          <CommandGroup>
            {filter.options.map((option) => (
              <CommandItem
                key={option}
                value={option}
                onSelect={() => {
                  filter.onChange(option);
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    filter.value === option ? "opacity-100" : "opacity-0",
                  )}
                />
                {option}
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </Command>
    </div>
  );
}

function FilterForm({ filter, label }: { filter: Input["filter"]; label: string }) {
  const form = useForm({
    defaultValues: {
      value: filter.value || "",
    },
  });

  const onSubmit = (data: { value: string }) => {
    filter.onChange(data.value);
  };

  if (filter.type === "combobox") {
    return <ComboboxFilterForm filter={filter} label={label} />;
  }

  return (
    <div className="flex flex-col gap-2 p-4">
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="space-y-2 pb-4">
          <Label htmlFor="value">Filter by {label}</Label>
          {filter.type === "text" && <Input {...form.register("value")} />}
          {filter.type === "select" && (
            <Select
              {...form.register("value")}
              defaultValue={filter.value || undefined}
              onValueChange={async (v) => {
                await form.setValue("value", v);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select an option" />
              </SelectTrigger>
              <SelectContent>
                {filter.options.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        <Button type="submit" className="w-full">
          Apply
        </Button>
      </form>
    </div>
  );
}

function InactiveFilter({
  label,
  filter,
}: {
  label: string;
  filter: Input["filter"];
  onActivate: () => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="xs"
          className="h-7 border-dashed rounded-2xl shadow-none border-muted-foreground/50"
        >
          <PlusCircle className="size-4 text-muted-foreground" />
          <span className="text-muted-foreground pr-0.5">{label}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <FilterForm filter={filter} label={label} />
      </PopoverContent>
    </Popover>
  );
}

export function SimpleFilter({ filter, label }: Input) {
  const [open, setOpen] = useState(false);

  if (filter.value === null) {
    return <InactiveFilter label={label} filter={filter} onActivate={() => setOpen(true)} />;
  }

  return (
    <div className="flex">
      <Button
        variant="outline"
        size="xs"
        className="h-7 rounded-l-2xl rounded-r-none border-r-0"
        onClick={(e) => {
          e.stopPropagation();
          filter.onClear();
        }}
      >
        <XCircle className="size-4" />
        <span>{label}</span>
      </Button>
      <Separator orientation="vertical" />

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="xs"
            className="h-7 rounded-r-2xl rounded-l-none border-l-0"
          >
            <span className="text-primary font-bold">{filter.displayValue || filter.value}</span>
            <ChevronDown className="size-4 text-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0" align="start">
          <FilterForm filter={filter} label={label} />
        </PopoverContent>
      </Popover>
    </div>
  );
}
